import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type PagePerfPage, type PagePerfAction } from '../../api/adminApi';

type TimeRange = 1 | 6 | 24 | 168;
const TIME_RANGES: { label: string; value: TimeRange }[] = [
  { label: '1h', value: 1 },
  { label: '6h', value: 6 },
  { label: '24h', value: 24 },
  { label: '7d', value: 168 },
];

const pct = (r: number) => `${Math.round(r * 100)}%`;
const slow = (ms: number) => ms >= 1500;

/** The portals, by the client-log `source` they report. Read-friendly names:
 *  nobody thinks of teacher.hindaun.in as "web-teacher". */
const PORTAL_LABEL: Record<string, string> = {
  'web-teacher': 'Teacher',
  'web-student': 'Student',
  'web-admin': 'Admin',
  unknown: 'Unknown',
};
const portalLabel = (source: string) => PORTAL_LABEL[source] ?? source;

type PortalFilter = 'all' | string;
const PORTAL_FILTERS: { label: string; value: PortalFilter }[] = [
  { label: 'All portals', value: 'all' },
  { label: 'Teacher', value: 'web-teacher' },
  { label: 'Student', value: 'web-student' },
  { label: 'Admin', value: 'web-admin' },
];

export function PagePerf() {
  const [hours, setHours] = useState<TimeRange>(24);
  const [portal, setPortal] = useState<PortalFilter>('all');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [expandedAction, setExpandedAction] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['page-perf', hours],
    queryFn: () => adminApi.getPagePerf(hours),
    refetchInterval: 60_000,
  });

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2>Page Perf</h2>
        <p className="section-description">
          Client-side timing, p50 / p95 over the window. <strong>Pages</strong> is page-load time
          (mount → data ready) with a per-component drill-down. <strong>Actions</strong> is what a
          user waits through after pressing Save — expand a row to see how much of it was the
          request and how much was the refetch + re-render afterwards.
          {' '}Every row is scoped to one <strong>portal</strong>: the same route label exists in
          more than one app (<code>/assignments</code> in teacher and student,
          {' '}<code>/questions</code> in teacher and admin), so blending them would let a fast
          portal hide a slow one. Rows recorded before the portal was stored show as
          {' '}<em>Unknown</em> and are still blended.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {TIME_RANGES.map(r => (
          <button
            key={r.value}
            className={`settings-tab ${hours === r.value ? 'active' : ''}`}
            onClick={() => setHours(r.value)}
            style={{ padding: '6px 16px', fontSize: 13 }}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {PORTAL_FILTERS.map(f => (
          <button
            key={f.value}
            className={`settings-tab ${portal === f.value ? 'active' : ''}`}
            onClick={() => setPortal(f.value)}
            style={{ padding: '6px 16px', fontSize: 13 }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading && <div className="loading">Loading page perf...</div>}
      {error && <div className="error">Error: {(error as Error).message}</div>}

      {data && (() => {
        const keep = <T extends { source: string }>(rows: T[]) =>
          portal === 'all' ? rows : rows.filter(r => r.source === portal);
        const pages = keep(data.pages);
        const actions = keep(data.actions ?? []);
        return (
        <>
          <div style={{ display: 'flex', gap: 24, marginBottom: 20, flexWrap: 'wrap' }}>
            <SummaryCard
              label="Loads"
              value={pages.reduce((n, p) => n + p.loadCount, 0).toLocaleString()}
            />
            <SummaryCard label="Pages" value={String(pages.length)} />
            <SummaryCard
              label="Actions"
              value={actions.reduce((n, a) => n + a.count, 0).toLocaleString()}
            />
          </div>

          <h3 style={{ fontSize: 14, margin: '0 0 8px', color: 'var(--text-secondary)' }}>Pages</h3>
          {pages.length === 0 ? (
            <p style={{ color: 'var(--text-tertiary)' }}>No page-load samples for this period.</p>
          ) : (
            <table className="admin-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: 24 }}></th>
                  <th style={{ width: 80 }}>Portal</th>
                  <th>Page</th>
                  <th style={{ textAlign: 'right' }}>Loads</th>
                  <th style={{ textAlign: 'right' }} title="Loads where the page fetched (not fully cached)">Cold</th>
                  <th style={{ textAlign: 'right' }}>p50</th>
                  <th style={{ textAlign: 'right' }}>p95</th>
                </tr>
              </thead>
              <tbody>
                {pages.map(p => {
                  // Keyed by portal AND route: the same route label appears once
                  // per portal now, so the route alone would collide.
                  const key = `${p.source} ${p.page}`;
                  return (
                    <PageRow
                      key={key}
                      page={p}
                      expanded={expanded === key}
                      onToggle={() => setExpanded(expanded === key ? null : key)}
                    />
                  );
                })}
              </tbody>
            </table>
          )}

          <h3 style={{ fontSize: 14, margin: '24px 0 8px', color: 'var(--text-secondary)' }}>Actions</h3>
          {actions.length === 0 ? (
            <p style={{ color: 'var(--text-tertiary)' }}>No action samples for this period.</p>
          ) : (
            <table className="admin-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: 24 }}></th>
                  <th style={{ width: 80 }}>Portal</th>
                  <th>Action</th>
                  <th>Page</th>
                  <th style={{ textAlign: 'right' }}>Count</th>
                  <th style={{ textAlign: 'right' }} title="Actions that failed">Failed</th>
                  <th style={{ textAlign: 'right' }}>p50</th>
                  <th style={{ textAlign: 'right' }}>p95</th>
                </tr>
              </thead>
              <tbody>
                {actions.map(a => {
                  const key = `${a.source} ${a.page} ${a.action}`;
                  return (
                    <ActionRow
                      key={key}
                      action={a}
                      expanded={expandedAction === key}
                      onToggle={() => setExpandedAction(expandedAction === key ? null : key)}
                    />
                  );
                })}
              </tbody>
            </table>
          )}
        </>
        );
      })()}
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: 'var(--bg-tertiary)', borderRadius: 8, padding: '12px 20px', minWidth: 130 }}>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)' }}>{value}</div>
    </div>
  );
}

function PageRow({ page, expanded, onToggle }: { page: PagePerfPage; expanded: boolean; onToggle: () => void }) {
  const hasComponents = page.components.length > 0;
  return (
    <>
      <tr onClick={hasComponents ? onToggle : undefined} style={{ cursor: hasComponents ? 'pointer' : 'default' }}>
        <td style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>
          {hasComponents ? (expanded ? '▾' : '▸') : ''}
        </td>
        <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{portalLabel(page.source)}</td>
        <td><code style={{ fontSize: 13 }}>{page.page}</code></td>
        <td style={{ textAlign: 'right' }}>
          {page.loadCount}
          {page.cappedCount > 0 && (
            <span
              style={{ marginLeft: 6, color: 'var(--text-tertiary)', fontSize: 12 }}
              title={`${page.cappedCount} load(s) never settled and were capped — their durations are a floor, not the real value`}
            >
              ({page.cappedCount} capped)
            </span>
          )}
        </td>
        <td style={{ textAlign: 'right', color: 'var(--text-tertiary)' }}>{page.coldCount}</td>
        <td style={{ textAlign: 'right' }}>{page.p50Ms}ms</td>
        <td style={{ textAlign: 'right', fontWeight: 600, color: slow(page.p95Ms) ? 'var(--color-error)' : 'var(--text-primary)' }}>
          {page.p95Ms}ms
        </td>
      </tr>
      {expanded && page.components.map(c => (
        <tr key={c.name} style={{ background: 'var(--bg-tertiary)', fontSize: 13 }}>
          <td></td>
          <td></td>
          <td style={{ paddingLeft: 32, color: 'var(--text-secondary)' }}>{c.name}</td>
          <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{c.count}</td>
          <td style={{ textAlign: 'right', color: 'var(--text-tertiary)' }} title="cache hit rate">{pct(c.cacheRate)}</td>
          <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{c.p50Ms}ms</td>
          <td style={{ textAlign: 'right', color: slow(c.p95Ms) ? 'var(--color-error)' : 'var(--text-secondary)' }}>{c.p95Ms}ms</td>
        </tr>
      ))}
    </>
  );
}

/** Human label for the two halves an action is split into — bare "request" /
 *  "settle" doesn't say which one implicates the backend. */
const ACTION_PHASE_LABEL: Record<string, string> = {
  request: 'request (network + server)',
  settle: 'settle (refetch + re-render)',
};

function ActionRow({ action, expanded, onToggle }: { action: PagePerfAction; expanded: boolean; onToggle: () => void }) {
  const hasComponents = action.components.length > 0;
  return (
    <>
      <tr onClick={hasComponents ? onToggle : undefined} style={{ cursor: hasComponents ? 'pointer' : 'default' }}>
        <td style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>
          {hasComponents ? (expanded ? '▾' : '▸') : ''}
        </td>
        <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{portalLabel(action.source)}</td>
        <td><code style={{ fontSize: 13 }}>{action.action}</code></td>
        <td style={{ color: 'var(--text-secondary)' }}><code style={{ fontSize: 13 }}>{action.page}</code></td>
        <td style={{ textAlign: 'right' }}>
          {action.count}
          {action.cappedCount > 0 && (
            <span
              style={{ marginLeft: 6, color: 'var(--text-tertiary)', fontSize: 12 }}
              title={`${action.cappedCount} sample(s) hit the settle cap — their durations are a floor, not the real value`}
            >
              ({action.cappedCount} capped)
            </span>
          )}
        </td>
        <td style={{ textAlign: 'right', color: action.failCount ? 'var(--color-error)' : 'var(--text-tertiary)' }}>
          {action.failCount}
        </td>
        <td style={{ textAlign: 'right' }}>{action.p50Ms}ms</td>
        <td style={{ textAlign: 'right', fontWeight: 600, color: slow(action.p95Ms) ? 'var(--color-error)' : 'var(--text-primary)' }}>
          {action.p95Ms}ms
        </td>
      </tr>
      {expanded && action.components.map(c => (
        <tr key={c.name} style={{ background: 'var(--bg-tertiary)', fontSize: 13 }}>
          <td></td>
          <td></td>
          <td colSpan={2} style={{ paddingLeft: 32, color: 'var(--text-secondary)' }}>
            {ACTION_PHASE_LABEL[c.name] || c.name}
          </td>
          <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{c.count}</td>
          <td></td>
          <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{c.p50Ms}ms</td>
          <td style={{ textAlign: 'right', color: slow(c.p95Ms) ? 'var(--color-error)' : 'var(--text-secondary)' }}>{c.p95Ms}ms</td>
        </tr>
      ))}
    </>
  );
}
